import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/lib/session';
import {
  type SetContactConsentInput,
  useContactConsent,
  useSetContactConsent,
} from '../api/contact-consent';
import { CustomerContactConsent } from './customer-contact-consent';

export function ConnectedContactConsent({ leadId }: { leadId: string }) {
  const { data } = useSession();
  const user = data?.user;
  if (!user?.id || !user.organizationId) return null;
  return (
    <ConsentEditor
      key={`${user.organizationId}:${user.id}:${leadId}`}
      leadId={leadId}
      actorId={user.id}
      organizationId={user.organizationId}
    />
  );
}
function ConsentEditor({
  leadId,
  actorId,
  organizationId,
}: { leadId: string; actorId: string; organizationId: string }) {
  const scope = { organizationId, actorId, leadId };
  const query = useContactConsent(scope);
  const save = useSetContactConsent(scope, {
    onError: () => {
      void query.refetch();
    },
  });
  if (query.isPending) return <Skeleton className="h-48 w-full" />;
  if (query.isError || !query.data)
    return (
      <div role="alert" className="space-y-2 text-sm">
        <p>Contact permission could not be loaded.</p>
        <Button variant="link" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  return (
    <div className="space-y-3">
      <CustomerContactConsent
        values={query.data.values}
        available={query.data.available}
        disabled={save.isPending || query.isFetching}
        onChange={(channel, enabled) => {
          const change: SetContactConsentInput = {
            channel,
            enabled,
            expectedVersion: query.data.version,
          };
          save.mutate(change);
        }}
      />
      {save.isPending && (
        <p role="status" className="text-muted-foreground text-sm">
          Saving…
        </p>
      )}
      {save.isError && (
        <p role="alert" className="text-destructive text-sm">
          {save.error.message ||
            'Permission could not be saved. Please try again.'}
        </p>
      )}
    </div>
  );
}
